import { Button, Flex, Title } from "@qws-org/ui-kit";
import type { FC } from "react";

import { isExternalLink, isInternalLink } from "~/core/utils/action.utils";
import { ColumnElement } from "~/features/steps/components/ColumnElement";

import type { StepsProps } from "../Steps";
import { ColumnCard } from "./ColumnCard";

export const StepsColumn: FC<StepsProps> = (props) => {
  const { data, getUrl, onCallAction, isControlInactive, ...rest } = props;
  const controlAction = data?.action;
  const action = data?.action?.action?.[0];

  let href;
  if (isExternalLink(action?.item)) {
    href = action.item.href ?? undefined;
  }
  if (isInternalLink(action?.item)) {
    href = action.item.page ? getUrl(action.item.page) : undefined;
  }

  const handlePressButton = (): void => {
    if (!controlAction || !action) return;

    const isInactive = isControlInactive(controlAction);
    if (!isInactive) {
      onCallAction(controlAction);
    }
  };

  return (
    <Flex
      justify="center"
      flexDirection="column"
      align="center"
      p={{ py: 40, px: 15 }}
      m={{ mx: "auto" }}
      maxWidth={1270}
      {...rest}
      d={{ p: { py: 80 }, m: { mx: "auto" }, ...rest.d }}
      data-testid="steps"
    >
      <Title
        as="h3"
        size="medium"
        fontWeight="mobile.bold"
        horizontalAlign="center"
        p={{ pb: 32 }}
        d={{ p: { pb: 64 } }}
      >
        {data.title}
      </Title>

      {!data.elements && data.steps_items && (
        <Flex>
          <Flex
            gap={24}
            flexDirection="column"
            align="center"
            d={{ gap: 20, flexDirection: "row" }}
          >
            {data.steps_items.map((item, index) => (
              <ColumnCard index={index + 1} text={item?.text ?? ""} />
            ))}
          </Flex>
        </Flex>
      )}

      {data.elements && (
        <Flex
          gap={24}
          flexDirection="column"
          align="stretch"
          justify="center"
          flexWrap="wrap"
          d={{ gap: 20, flexDirection: "row" }}
        >
          {data.elements.map((item, index) => (
            <ColumnElement key={index} {...item?.steps_elements_id} />
          ))}
        </Flex>
      )}

      {data.controls ? (
        data.controls
          .filter((control) => {
            if (control?.steps_controls_id?.action) {
              return isControlInactive(control.steps_controls_id.action);
            }
            return true;
          })
          .map((control, idx) => {
            const buttonControlAction = control?.steps_controls_id?.action;
            const buttonAction = buttonControlAction?.action?.[0];
            let buttonHref;
            if (isExternalLink(buttonAction?.item)) {
              buttonHref = buttonAction.item.href ?? undefined;
            }
            if (isInternalLink(buttonAction?.item)) {
              buttonHref = buttonAction.item.page
                ? getUrl(buttonAction.item.page)
                : undefined;
            }

            return (
              <Button
                href={buttonHref}
                key={control?.id}
                size="small"
                fontWeight="desktop.bold"
                horizontalAlign="center"
                md={{
                  fontSize: "button.desktop.medium",
                  lineHeight: 'button.desktop.medium"',
                }}
                m={{ mt: 36 }}
                p={{ py: 12, px: 24 }}
                d={{ m: { mt: 80 }, p: { px: 48, py: 16 } }}
                onPress={() => {
                  if (buttonControlAction) {
                    onCallAction(buttonControlAction);
                  }
                }}
                data-testid={`steps-control-${idx}`}
              >
                {control?.steps_controls_id?.text}
              </Button>
            );
          })
      ) : (
        <Button
          href={href}
          onPress={handlePressButton}
          size="small"
          fontWeight="desktop.bold"
          horizontalAlign="center"
          md={{
            fontSize: "button.desktop.medium",
            lineHeight: 'button.desktop.medium"',
          }}
          m={{ mt: 36 }}
          p={{ py: 12, px: 24 }}
          d={{ m: { mt: 80 }, p: { px: 48, py: 16 } }}
          data-testid="steps-control-0"
        >
          {data.action_text}
        </Button>
      )}
    </Flex>
  );
};
